import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Shield, FolderPlus, Play, CheckCircle2, ArrowRight } from 'lucide-react';

const steps = [
  {
    icon: Shield,
    title: 'Welcome to Trinetra!',
    description: 'We help you find and fix security issues in your website or app -- no security expertise needed. Everything is explained in plain language.',
  },
  {
    icon: FolderPlus,
    title: 'Add Your First Project',
    description: 'Just paste your website URL and give it a name. We\'ll handle the rest.',
  },
  {
    icon: Play,
    title: 'Run Your First Scan',
    description: 'With one click, we\'ll check your site for common security issues. It usually takes 2-5 minutes.',
  },
  {
    icon: CheckCircle2,
    title: 'Review & Fix',
    description: 'We\'ll show you what we found in easy-to-understand language, with step-by-step instructions to fix each issue.',
  },
];

export function OnboardingPage() {
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();

  const step = steps[currentStep];
  const isLast = currentStep === steps.length - 1;

  return (
    <div className="min-h-[80vh] flex items-center justify-center">
      <Card className="max-w-lg w-full p-8 text-center">
        {/* Step indicator */}
        <div className="flex justify-center gap-2 mb-8">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`h-2 rounded-full transition-all ${
                i === currentStep ? 'w-8 bg-blue-600' : i < currentStep ? 'w-2 bg-blue-300' : 'w-2 bg-gray-200 dark:bg-muted'
              }`}
            />
          ))}
        </div>

        {/* Icon */}
        <div className="h-16 w-16 rounded-2xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center mx-auto mb-6">
          <step.icon className="h-8 w-8 text-blue-600" />
        </div>

        {/* Content */}
        <h2 className="text-xl font-bold text-gray-900 dark:text-foreground mb-3">{step.title}</h2>
        <p className="text-muted-foreground dark:text-muted-foreground mb-8 leading-relaxed">{step.description}</p>

        {/* Actions */}
        <div className="flex gap-3 justify-center">
          {currentStep > 0 && (
            <Button variant="outline" onClick={() => setCurrentStep(currentStep - 1)}>
              Back
            </Button>
          )}
          <Button
            onClick={() => {
              if (isLast) {
                navigate('/projects/new');
              } else {
                setCurrentStep(currentStep + 1);
              }
            }}
          >
            {isLast ? 'Get Started' : 'Next'}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>

        {/* Skip */}
        {!isLast && (
          <button
            className="text-sm text-muted-foreground dark:text-muted-foreground hover:text-muted-foreground dark:hover:text-foreground mt-4 transition-colors"
            onClick={() => navigate('/dashboard')}
          >
            Skip for now
          </button>
        )}
      </Card>
    </div>
  );
}
